/**
 * Storefront v6 — the cart drawer and the phone cart bar.
 *
 * Two kinds of lines share the drawer: the checkout cart (plans, passes,
 * catering — CartContext, paid at /customers/checkout) and the à-la-carte
 * lines (item ids + quantities the ORDER PAGE re-prices; the storefront
 * never invents a total for them). Mandatory charges are never hidden:
 * the drawer says where tax and delivery are added.
 */
import React, { useEffect } from 'react';
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { money, OrderNowButton } from '../primitives';
import { itemPrice } from '../sections/Products';
import StorefrontCartSummary, { groupCartOrders } from '../../../storefront/StorefrontCartSummary';
import { dateLabel } from '../../../storefront/MealPackBuilder';

export const CartDrawer = ({
  open, onClose, cartItems = [], onRemoveCartItem, menuLines = [], onMenuQty, currency, settings,
  onCheckout, onOrderDishes, subdomain,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);
  if (!open) return null;

  // v5.67.2 - a dish that sold out while it sat here stays VISIBLE and is said, but it
  // is not money: it leaves the estimate, the minimum-order maths and the handoff, and
  // "Continue to order" waits until at least one line can still be bought.
  const liveLines = menuLines.filter((l) => !l.soldOut);
  const menuEstimate = liveLines.reduce((s, l) => s + itemPrice(l.item) * l.qty, 0);
  const minOrder = Number(settings?.minimum_order_value) || 0;
  const menuQty = liveLines.reduce((s, l) => s + l.qty, 0);
  const empty = !cartItems.length && !menuLines.length;

  return (
    <>
      <div className="s6-backdrop" onClick={onClose} />
      <aside className="s6-drawer" role="dialog" aria-modal="true" aria-label="Your cart" data-testid="s6-cart-drawer">
        <div className="flex items-center justify-between px-5 h-16 border-b" style={{ borderColor: 'var(--s6-line)' }}>
          <h2 className="s6-h3 !text-lg flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Your cart</h2>
          <button type="button" onClick={onClose} className="s6-icon-btn" aria-label="Close cart" data-testid="s6-cart-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="s6-drawer-body px-5 py-4 space-y-6">
          {empty && (
            <p className="py-10 text-center" style={{ color: 'var(--s6-ink-muted)' }} data-testid="s6-cart-empty">Your cart is empty. Pick something delicious.</p>
          )}
          {menuLines.length > 0 && (
            <div data-testid="s6-cart-dishes">
              <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--s6-ink-muted)' }}>Dishes</p>
              <ul className="s6-rows">
                {menuLines.map(({ item, qty, soldOut }) => (
                  <li key={item.id} className="py-3 flex items-center gap-3" data-testid={`s6-cart-line-${item.id}`}>
                    {item.image_url && <img src={item.image_url} alt="" className={`w-14 h-14 rounded-[10px] object-cover flex-shrink-0 ${soldOut ? 's6-thumb-soldout' : ''}`} />}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{item.name}</p>
                      {soldOut ? (
                        <p className="text-sm font-semibold" style={{ color: 'var(--s6-closed)' }} data-testid={`s6-cart-soldout-${item.id}`}>Sold out since you added it</p>
                      ) : (
                        <p className="text-sm" style={{ color: 'var(--s6-ink-muted)' }}>{money(itemPrice(item), currency)} each</p>
                      )}
                    </div>
                    {soldOut ? (
                      <button type="button" onClick={() => onMenuQty(item, -qty)} className="s6-icon-btn !w-9 !h-9" aria-label={`Remove ${item.name}`} data-testid={`s6-cart-remove-${item.id}`}><Trash2 className="w-4 h-4" /></button>
                    ) : (
                      <div className="s6-stepper">
                        <button type="button" onClick={() => onMenuQty(item, -1)} aria-label={`Remove one ${item.name}`}><Minus className="w-4 h-4" /></button>
                        <span>{qty}</span>
                        <button type="button" onClick={() => onMenuQty(item, 1)} aria-label={`Add one more ${item.name}`}><Plus className="w-4 h-4" /></button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs" style={{ color: 'var(--s6-ink-muted)' }}>Sizes, extras, tax and delivery are set on the order page.</p>
            </div>
          )}
          {cartItems.length > 0 && (
            <div data-testid="s6-cart-plans">
              <StorefrontCartSummary cart groups={groupCartOrders(cartItems)} payload={{ customer_info: {}, origin_url: window.location.origin, subdomain, currency }} />
              <details className="mt-4"><summary className="cursor-pointer text-sm font-medium" style={{ color: 'var(--s6-ink-muted)' }}>View or edit meals</summary>
              <ul className="s6-rows">
                {cartItems.map((it) => (
                  <li key={it.id} className="py-3 flex items-center gap-3" data-testid={`s6-cart-item-${it.id}`}>
                    {it.image && <img src={it.image} alt="" className="w-14 h-14 rounded-[10px] object-cover flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{it.meal_plan_name}</p>
                      <p className="text-sm" style={{ color: 'var(--s6-ink-muted)' }}>
                        {it.is_meal_pass ? `${it.total_credits} credits` : it.is_catering ? `${it.persons} guests · ${it.event_date}` : `${it.pack_size ? `${it.pack_size} meals · one delivery` : `${it.quantity} deliveries`}${it.recurring ? ' · recurring' : ''}`}
                      </p>
                      {it.pack_size && <ul className="mt-2 text-xs" style={{ color: 'var(--s6-ink-muted)' }}>{it.selected_meal_names?.map(pick => <li key={pick.item_id}>{pick.quantity}× {pick.name}</li>)}</ul>}
                      {it.selected_addon_names?.length > 0 && <ul className="mt-2 text-xs"><li className="font-semibold">Add-ons included</li>{it.selected_addon_names.map((extra, index) => <li key={extra.plan_id || index}>{extra.quantity}× {extra.name} · {money(extra.price, it.currency || currency)}</li>)}</ul>}
                      {it.start_date && <p className="mt-2 text-xs">Delivery: {dateLabel(it.start_date)}</p>}
                    </div>
                    <span className="font-semibold tabular-nums">{money(it.price, it.currency || currency)}</span>
                    <button type="button" onClick={() => onRemoveCartItem(it.id)} className="s6-icon-btn !w-9 !h-9" aria-label={`Remove ${it.meal_plan_name}`}><Trash2 className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
              </details>
            </div>
          )}
        </div>
        {!empty && (
          <div className="s6-drawer-foot space-y-3">
            {liveLines.length > 0 && (
              <>
                <div className="flex justify-between text-sm"><span style={{ color: 'var(--s6-ink-muted)' }}>Dishes ({menuQty})</span><span className="font-semibold tabular-nums">{money(menuEstimate, currency)}</span></div>
                {minOrder > 0 && menuEstimate < minOrder && (
                  <p className="text-xs" style={{ color: 'var(--s6-warn)' }} data-testid="s6-cart-minimum">Add {money(minOrder - menuEstimate, currency)} more to reach the {money(minOrder, currency)} minimum.</p>
                )}
                <OrderNowButton className="w-full" onClick={onOrderDishes} data-testid="s6-cart-order-dishes">Continue to order</OrderNowButton>
              </>
            )}
            {cartItems.length > 0 && (
              <>
                <p className="text-xs" style={{ color: 'var(--s6-ink-muted)' }}>Tax and delivery are shown before you pay.</p>
                <button type="button" onClick={onCheckout} className="s6-btn s6-btn-block" data-testid="s6-cart-checkout">Checkout <ArrowRight className="w-4 h-4" /></button>
              </>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

/** The phone bar once the cart has anything in it. */
export const CartBar = ({ count = 0, total = 0, currency, onOpen }) => {
  if (!count) return null;
  return (
    <div className="s6-cart-bar" data-testid="s6-cart-bar">
      <button type="button" onClick={onOpen} className="s6-btn s6-btn-order w-full !min-h-[54px] justify-between px-5">
        <span className="inline-flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> View cart · {count} {count === 1 ? 'item' : 'items'}</span>
        <span className="tabular-nums">{total > 0 ? `Items ${money(total, currency)}` : ''}</span>
      </button>
    </div>
  );
};
